/**
 * Conversation Manager Component
 * Handles user input and conversation flow using the state machine
 */

import React from 'react'
import type { ConversationState, InputType, ConversationStep } from '../types'
import { ButtonInput } from './ButtonInput'
import { TextInput } from './TextInput'
import { CalendlyEmbed } from './CalendlyEmbed'
import { getStepDefinition } from '../engine/steps'
import styles from './ConversationManager.module.css'

export interface ConversationManagerProps {
  conversationState: ConversationState
  onUserInput: (value: string) => void
  primaryColor?: string
}

/**
 * Renders the appropriate input component based on current conversation step
 */
export const ConversationManager: React.FC<ConversationManagerProps> = ({
  conversationState,
  onUserInput,
  primaryColor = '#0066CC',
}) => {
  const { step, leadData } = conversationState

  // If conversation is complete, don't show input
  if (step === 'thank_you') {
    return null
  }

  const stepDef = getStepDefinition(step as ConversationStep)
  const { inputType, options } = stepDef

  const handleInput = (value: string) => {
    onUserInput(value)
  }

  // Render appropriate input component
  switch (inputType) {
    case 'buttons':
      if (!options || options.length === 0) {
        return null
      }
      return (
        <div className={styles.conversationManager} data-testid="conversation-manager">
          <ButtonInput
            options={options}
            onSelect={handleInput}
            primaryColor={primaryColor}
          />
        </div>
      )

    case 'text':
    case 'email':
    case 'phone':
      return (
        <div className={styles.conversationManager} data-testid="conversation-manager">
          <TextInput
            type={inputType}
            placeholder={getPlaceholder(inputType)}
            onSubmit={handleInput}
            {...(stepDef.validator ? { validate: stepDef.validator } : {})}
            primaryColor={primaryColor}
          />
        </div>
      )

    case 'calendly': {
      // Get Calendly URL from lead data or client config
      const calendlyUrl = leadData.calendlyUrl as string | undefined || 'https://calendly.com/example'

      // Build prefill data from collected lead information (only include defined values)
      const prefill: { name?: string; email?: string; phone?: string } = {}
      if (leadData.name && typeof leadData.name === 'string') {
        prefill.name = leadData.name
      }
      if (leadData.email && typeof leadData.email === 'string') {
        prefill.email = leadData.email
      }
      if (leadData.phone && typeof leadData.phone === 'string') {
        prefill.phone = leadData.phone
      }

      return (
        <div className={styles.conversationManager} data-testid="conversation-manager">
          <CalendlyEmbed
            url={calendlyUrl}
            onBookingComplete={handleInput}
            prefill={prefill}
          />
        </div>
      )
    }

    default:
      return null
  }
}

/**
 * Get placeholder text for input types
 */
function getPlaceholder(inputType: InputType): string {
  switch (inputType) {
    case 'email':
      return 'your@email.com'
    case 'phone':
      return '(555) 123-4567'
    case 'text':
      return 'Type your answer...'
    default:
      return ''
  }
}
